import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Box, Typography, Button, CircularProgress } from '@mui/material';
import { X, ChevronLeft } from 'lucide-react';
import { gamesService } from 'components/api/games';
import { Alert } from 'components/alert/alert';
import SearchBar from 'components/common/SearchBar/SearchBar';
import styles from './FreeSpinGameSelectModal.module.scss';

interface FreeSpinGameSelectModalProps {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  onSelect: (gameId: number, betLevel: number) => void;
}

interface Game {
  id: number;
  title: string;
  imageUrl: string;
}

interface BetLevel {
  betLevel: number;
  amount: string;
  amountRaw: string;
}

const FreeSpinGameSelectModal: React.FC<FreeSpinGameSelectModalProps> = ({
  open,
  siteId,
  onClose,
  onSelect,
}) => {
  const [step, setStep] = useState<1 | 2>(1); // 1: Select Game, 2: Select Bet Level

  // Games state
  const [games, setGames] = useState<Game[]>([]);
  const [page, setPage] = useState(1);
  const [loadingGames, setLoadingGames] = useState(false);
  const [hasMoreGames, setHasMoreGames] = useState(true);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isInitialMount = useRef(true);

  // Bet levels state
  const [betLevels, setBetLevels] = useState<BetLevel[]>([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [selectedBetLevel, setSelectedBetLevel] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setGames([]);
      setPage(1);
      setHasMoreGames(true);
      setSelectedGame(null);
      setSelectedBetLevel(null);
      setError(null);
      setSearchTerm('');
      setDebouncedSearch('');
      fetchGames(1, true, '');
    }
  }, [open]);

  // Refetch games when debounced search changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!open || step !== 1) return;
    setPage(1);
    fetchGames(1, true, debouncedSearch);
  }, [debouncedSearch]);

  const fetchGames = async (pageNum: number, isInitial = false, searchStr = debouncedSearch) => {
    setLoadingGames(true);
    setError(null);
    try {
      const res = await gamesService.getFreeSpinsGames({
        page: pageNum,
        limit: 15,
        sortField: 'id',
        sortValue: 'DESC',
        siteId,
        ...(searchStr.trim() ? { search: searchStr.trim() } : {}),
      });

      const responseData =
        res.data?.data?.docs ||
        res.data?.docs ||
        res.data?.data ||
        res.data?.sessions ||
        res.data ||
        res;
      const newGames = Array.isArray(responseData) ? responseData : responseData.data || [];

      if (isInitial) {
        setGames(Array.isArray(newGames) ? newGames : []);
      } else {
        setGames((prev) => [...prev, ...(Array.isArray(newGames) ? newGames : [])]);
      }

      // Update hasMoreGames based on totalPages if available, or fallback to length check
      const totalPages = res.data?.data?.totalPages || res.data?.totalPages;
      if (totalPages) {
        setHasMoreGames(pageNum < totalPages);
      } else if (newGames.length < 15) {
        setHasMoreGames(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load games');
    } finally {
      setLoadingGames(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGames(nextPage, false, debouncedSearch);
  };

  const handleGameClick = async (game: Game) => {
    setSelectedGame(game);
    setStep(2);
    setLoadingBets(true);
    setError(null);
    setSelectedBetLevel(null);
    try {
      const res = await gamesService.getGameBetLevels(game.id, siteId, 'USD');

      const currencies = res.data?.data?.currencies || res.data?.currencies || [];
      const usdCurrency = currencies.find((c: any) => c.currency === 'USD');
      const levels = usdCurrency?.betLevels || [];

      setBetLevels(Array.isArray(levels) ? levels : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load bet levels');
    } finally {
      setLoadingBets(false);
    }
  };

  const handleConfirm = () => {
    if (selectedGame && selectedBetLevel !== null) {
      onSelect(selectedGame.id, selectedBetLevel);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          margin: 2,
        },
      }}
    >
      <Box className={styles.modalContent}>
        <Box
          className={styles.modalHeader}
          sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1.5 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {step === 2 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <button className={styles.closeBtn} onClick={() => setStep(1)}>
                  <ChevronLeft size={20} />
                </button>
                <h2>Select Bet Level for {selectedGame?.title}</h2>
              </Box>
            ) : (
              <h2>Select Free Spin Game</h2>
            )}
            <button className={styles.closeBtn} onClick={onClose}>
              <X size={20} />
            </button>
          </Box>

          {step === 1 && (
            <SearchBar placeholder="Search game..." value={searchTerm} onChange={setSearchTerm} />
          )}
        </Box>

        <Box className={styles.modalBody}>
          {error && <Alert message={error} type="error" />}

          {step === 1 && (
            <>
              {games.length === 0 && loadingGames ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={30} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              ) : games.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 4,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Typography variant="body2">
                    {debouncedSearch
                      ? `No games found matching "${debouncedSearch}"`
                      : 'No games available'}
                  </Typography>
                </Box>
              ) : (
                <Box className={styles.gamesGrid}>
                  {games.map((game) => (
                    <Box
                      key={game.id}
                      onClick={() => handleGameClick(game)}
                      sx={{
                        borderRadius: '8px',
                        height: 200,
                        border: '2px solid var(--border-color)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: 'var(--bg-input)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'var(--accent-blue)',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', height: 162, overflow: 'hidden' }}>
                        <img
                          src={game.imageUrl}
                          alt={game.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://via.placeholder.com/120?text=No+Image';
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="primary">
                          ID: {game.id}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {hasMoreGames && !loadingGames && games.length > 0 && (
                <Box className={styles.loadMoreContainer}>
                  <Button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                    variant="outlined"
                  >
                    Load More Games
                  </Button>
                </Box>
              )}
              {loadingGames && games.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {loadingBets ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={30} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              ) : betLevels.length === 0 ? (
                <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                  No bet levels available for this game.
                </Typography>
              ) : (
                <Box className={styles.betLevelsGrid}>
                  {betLevels.map((bet) => (
                    <div
                      key={bet.betLevel}
                      className={`${styles.betLevelBtn} ${selectedBetLevel === bet.betLevel ? styles.selected : ''}`}
                      onClick={() => setSelectedBetLevel(bet.betLevel)}
                    >
                      <div>Level {bet.betLevel}</div>
                      <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
                        ${bet.amount}
                      </div>
                    </div>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        {step === 2 && (
          <Box className={styles.modalFooter}>
            <Button className={styles.cancelBtn} onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              className={styles.confirmBtn}
              variant="contained"
              disabled={selectedBetLevel === null}
              onClick={handleConfirm}
            >
              Selected
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default FreeSpinGameSelectModal;
